import { useEffect, useState, useCallback } from 'react';
import type { OrderItem, OrderListRequest } from '../types/orders';
import { ordersService } from '../services/orders';
import { format } from 'date-fns';

interface UseOrdersParams {
  userId: string;
  storeId: string;
  shipmentStatus: string;
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  enabled?: boolean;
  isAdmin?: boolean;
  startDate?: Date | null;
  endDate?: Date | null;
  channelFilter?: string;
  paymentFilter?: string;
  courierFilter?: string;
  shippingModeFilter?: string;
  zoneFilter?: string;
}

export function useOrders({
  userId,
  storeId,
  shipmentStatus,
  page = 1,
  pageSize = 50,
  searchTerm,
  enabled = true,
  isAdmin = false,
  startDate,
  endDate,
  channelFilter,
  paymentFilter,
  courierFilter,
  shippingModeFilter,
  zoneFilter,
}: UseOrdersParams) {
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    // Prevent fetching if only one date is selected (e.g., during date range selection)
    if ((startDate && !endDate) || (!startDate && endDate)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const baseBody: OrderListRequest = {
        user_id: userId,
        store_id: String(storeId),
        shipment_status: shipmentStatus === 'all' ? 'all' : shipmentStatus,
        page_size: pageSize,
        page,
        only_count: false,
        search: [],
        sorting: [
          {
            field: 'created_at',
            direction: 'DESC',
          },
        ],
      };

      if (startDate && endDate) {
        const d1Str = format(startDate, 'yyyy-MM-dd 00:00:00');
        const d2Str = format(endDate, 'yyyy-MM-dd 23:59:59');
        baseBody.search?.push({
          searchOn: 'date',
          searchTerm: `${d1Str},${d2Str}`,
        });
      }

      if (channelFilter) {
        baseBody.search?.push({ searchOn: 'channel', searchTerm: channelFilter });
      }
      if (paymentFilter) {
        baseBody.search?.push({ searchOn: 'payment_mode', searchTerm: paymentFilter });
      }
      if (courierFilter) {
        baseBody.search?.push({ searchOn: 'courier_name', searchTerm: courierFilter });
      }
      if (shippingModeFilter) {
        let modeSearchTerm = shippingModeFilter;
        if (shippingModeFilter.toLowerCase().includes('surface')) {
          modeSearchTerm = 'surface';
        } else if (shippingModeFilter.toLowerCase().includes('air')) {
          modeSearchTerm = 'air';
        }
        baseBody.search?.push({ searchOn: 'service_name', searchTerm: modeSearchTerm });
      }
      if (zoneFilter) {
        baseBody.search?.push({ searchOn: 'zone', searchTerm: zoneFilter });
      }

      if (searchTerm && searchTerm.trim().length > 0) {
        const term = searchTerm.trim();
        const isChannelOrderTab = shipmentStatus === 'pending' || shipmentStatus === 'failed';

        if (isChannelOrderTab) {
          // Channel Order / Failed tabs: single search call with order_number
          const body = {
            ...baseBody,
            search: [...(baseBody.search || []), { searchOn: 'order_number', searchTerm: term }],
          };
          const res = await ordersService.getOrders(userId, body);
          if (res.success) {
            setOrders(res.results || []);
            setTotalCount(res.total_count || 0);
          } else {
            setOrders([]);
            setTotalCount(0);
          }
        } else {
          // Other tabs: sequential multi-field search with original order
          const isLongNumeric = /^\d{10,25}$/.test(term);
          const searchFields = isLongNumeric
            ? ['awb_number', 'company_name', 'crn_number', 'order_number']
            : ['company_name', 'crn_number', 'order_number', 'awb_number'];

          for (const field of searchFields) {
            const body = {
              ...baseBody,
              search: [...(baseBody.search || []), { searchOn: field, searchTerm: term }],
            };

            const res = await ordersService.getOrders(userId, body);

            if (res.success && res.results && res.results.length > 0) {
              setOrders(res.results);
              setTotalCount(res.total_count);
              setLoading(false);
              return; // Found results, exit
            }
            // If this was the last field and we still have no results
            if (field === searchFields[searchFields.length - 1]) {
              setOrders([]);
              setTotalCount(0);
            }
          }
        }
      } else {
        const res = await ordersService.getOrders(userId, baseBody);
        if (res.success) {
          setOrders(res.results || []);
          setTotalCount(res.total_count || 0);
        } else {
          setError(res.message || 'Failed to fetch orders');
        }
      }
    } catch (err: any) {
      setError(err?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }, [userId, storeId, shipmentStatus, page, pageSize, searchTerm, isAdmin, startDate, endDate, channelFilter, paymentFilter, courierFilter, shippingModeFilter, zoneFilter]);

  useEffect(() => {
    if (!enabled) return;
    fetchOrders();
  }, [fetchOrders, enabled]);

  const handleCancelOrder = useCallback(
    async (shipmentId: string | number) => {
      try {
        const res = await ordersService.cancelOrder(shipmentId, userId);
        if (res.success === false && res.message) {
          throw new Error(res.message);
        }
        if (
          res.success ||
          res.code === 200 ||
          res?.message?.toLowerCase().includes('success')
        ) {
          await fetchOrders();
          return { success: true, data: res };
        }
        throw new Error(res.message || 'Failed to cancel order');
      } catch (err: any) {
        const errMsg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          (typeof err.response?.data === 'string' ? err.response.data : null) ||
          err.message ||
          'Failed to cancel order';
        await fetchOrders();
        throw new Error(errMsg);
      }
    },
    [userId, fetchOrders]
  );

  const handleGetManifest = useCallback(
    async (payload: any) => {
      try {
        const res = await ordersService.getManifest(userId, payload);
        if (res.success === false && res.message) {
          throw new Error(res.message);
        }
        if (
          (res.success || res.code === 200 || res.data || res.url) &&
          res.code !== 404
        ) {
          return { success: true, data: res };
        }
        throw new Error(res.message || 'Failed to get manifest');
      } catch (err: any) {
        throw new Error(
          err.response?.data?.message || err.message || 'Failed to get manifest'
        );
      }
    },
    [userId]
  );

  return {
    orders,
    totalCount,
    loading,
    error,
    refetch: fetchOrders,
    handleCancelOrder,
    handleGetManifest,
  };
}
